import React, { useState, useEffect } from 'react';
import { ShieldCheck, Settings, Sparkles, Scale, AlertTriangle, ArrowRight, Check, HelpCircle, RefreshCw, Box } from 'lucide-react';
import { calculateShippingCosts, DEFAULT_CURRENCY_RATES } from '../lib/shippingAndPayment';
import { ShippingCostCalculation } from '../lib/shippingAndPayment';
import { db } from '../lib/firebase';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';

export interface SafetyMarginConfig {
  marginPercentage: number; // e.g. 10 (%)
  packagingBufferGrams: number; // e.g. 250 (g)
}

const DEFAULT_SAFETY_MARGIN_CONFIG: SafetyMarginConfig = {
  marginPercentage: 10,
  packagingBufferGrams: 250
};

interface ShippingSafetyMarginSuggestionProps {
  netWeightKg: number;
  onApplySuggestedWeight?: (suggestedGrossWeightKg: number, recommendedMethodId?: string) => void;
  isAdmin?: boolean;
  className?: string;
  destinationCountry?: string;
  isFreeShipping?: boolean;
}

export const ShippingSafetyMarginSuggestion: React.FC<ShippingSafetyMarginSuggestionProps> = ({
  netWeightKg,
  onApplySuggestedWeight,
  isAdmin = true,
  className = '',
  destinationCountry = 'Brasil',
  isFreeShipping = false
}) => {
  // Load margin config from localStorage or fallback
  const [config, setConfig] = useState<SafetyMarginConfig>(() => {
    try {
      const saved = localStorage.getItem('shipping_safety_margin_config');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn('Error reading shipping safety margin config:', e);
    }
    return DEFAULT_SAFETY_MARGIN_CONFIG;
  });

  // Listen to Firestore shipping margin config
  useEffect(() => {
    try {
      const unsub = onSnapshot(doc(db, 'settings', 'shipping_margin'), (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as SafetyMarginConfig;
          setConfig(data);
          localStorage.setItem('shipping_safety_margin_config', JSON.stringify(data));
        }
      }, (err) => {
        console.warn('Firestore shipping margin config sync notice:', err);
      });
      return () => unsub();
    } catch (e) {
      console.warn(e);
    }
  }, []);

  const [isEditingAdminConfig, setIsEditingAdminConfig] = useState(false);
  const [tempPercentage, setTempPercentage] = useState(config.marginPercentage);
  const [tempBufferGrams, setTempBufferGrams] = useState(config.packagingBufferGrams);
  const [appliedNotice, setAppliedNotice] = useState(false);

  // Save config changes
  const handleSaveConfig = async () => {
    const newConfig: SafetyMarginConfig = {
      marginPercentage: Math.max(0, Math.min(50, tempPercentage)),
      packagingBufferGrams: Math.max(0, Math.min(2000, tempBufferGrams))
    };
    setConfig(newConfig);
    try {
      localStorage.setItem('shipping_safety_margin_config', JSON.stringify(newConfig));
      await setDoc(doc(db, 'settings', 'shipping_margin'), newConfig);
    } catch (e) {
      console.warn('Error saving shipping safety margin config:', e);
    }
    setIsEditingAdminConfig(false);
  };

  // Safe net weight
  const safeNetWeight = Math.max(0.1, netWeightKg || 0.1);

  // Calculate gross weight with margin
  const marginWeight = safeNetWeight * (config.marginPercentage / 100);
  const bufferKg = config.packagingBufferGrams / 1000;
  const grossWeightKg = parseFloat((safeNetWeight + marginWeight + bufferKg).toFixed(2));

  // Compute freight calculations for both Net and Gross weight
  const netCalculations = calculateShippingCosts(safeNetWeight, 1, 'free', destinationCountry, isFreeShipping);
  const grossCalculations = calculateShippingCosts(grossWeightKg, 1, 'free', destinationCountry, isFreeShipping);

  // Pick best eligible method (EMS or Small Packet)
  const recommendedGrossCalc = grossCalculations.find(c => c.isEligible && !c.requiresQuote) || grossCalculations[0];
  const recommendedNetCalc = netCalculations.find(c => c.method.id === recommendedGrossCalc?.method?.id) || netCalculations[0];

  const hasTierJump = recommendedNetCalc && recommendedGrossCalc && recommendedGrossCalc.costJpy > recommendedNetCalc.costJpy;

  const handleApply = () => {
    if (onApplySuggestedWeight) {
      onApplySuggestedWeight(grossWeightKg, recommendedGrossCalc?.method?.id);
      setAppliedNotice(true);
      setTimeout(() => setAppliedNotice(false), 3000);
    }
  };

  return (
    <div className={`bg-gradient-to-br from-amber-50/90 via-slate-50 to-sky-50 rounded-2xl border border-amber-200/80 p-4 sm:p-5 shadow-sm space-y-4 ${className}`}>
      
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200/60 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="p-2 bg-amber-500 text-white rounded-xl shadow-xs">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <h4 className="text-xs sm:text-sm font-extrabold text-slate-800 flex items-center gap-2">
              <span>Sugestão de Frete com Margem de Segurança</span>
              <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-black rounded-full border border-amber-300/60">
                +{config.marginPercentage}% + {config.packagingBufferGrams}g embalagem
              </span>
            </h4>
            <p className="text-[11px] text-slate-500">
              Cálculo automático de peso bruto com tolerância para caixa e proteção
            </p>
          </div>
        </div>

        {isAdmin && (
          <button
            type="button"
            onClick={() => {
              setTempPercentage(config.marginPercentage);
              setTempBufferGrams(config.packagingBufferGrams);
              setIsEditingAdminConfig(!isEditingAdminConfig);
            }}
            className="px-2.5 py-1 bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors shadow-2xs"
            title="Configurar margem de segurança (Painel Admin)"
          >
            <Settings className="w-3.5 h-3.5 text-amber-600" />
            <span>Configurar Margem</span>
          </button>
        )}
      </div>

      {/* Admin Inline Config Drawer */}
      {isEditingAdminConfig && (
        <div className="p-3.5 bg-white rounded-xl border border-amber-300 shadow-inner space-y-3 animate-fade-in text-xs">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <span className="font-bold text-slate-800 flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-amber-600" />
              Configuração de Margem de Segurança (Admin)
            </span>
            <span className="text-[10px] text-slate-400">Salvo no sistema</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">
                Porcentagem de Segurança (%)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="50"
                  value={tempPercentage}
                  onChange={(e) => setTempPercentage(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-1.5 rounded-lg border border-slate-300 font-bold font-mono text-slate-800 focus:ring-2 focus:ring-amber-500"
                />
                <span className="text-slate-500 font-bold">%</span>
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">
                Tolerância de Embalagem (Gramas)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="2000"
                  step="50"
                  value={tempBufferGrams}
                  onChange={(e) => setTempBufferGrams(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-1.5 rounded-lg border border-slate-300 font-bold font-mono text-slate-800 focus:ring-2 focus:ring-amber-500"
                />
                <span className="text-slate-500 font-bold">g</span>
              </div>
            </div>
          </div>

          {/* Presets */}
          <div className="flex items-center gap-2 pt-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase">Atalhos:</span>
            <button
              type="button"
              onClick={() => { setTempPercentage(5); setTempBufferGrams(150); }}
              className="px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-[10px] font-bold"
            >
              Leve (5% + 150g)
            </button>
            <button
              type="button"
              onClick={() => { setTempPercentage(10); setTempBufferGrams(250); }}
              className="px-2 py-0.5 bg-amber-100 hover:bg-amber-200 text-amber-900 rounded text-[10px] font-bold"
            >
              Padrão (10% + 250g)
            </button>
            <button
              type="button"
              onClick={() => { setTempPercentage(15); setTempBufferGrams(400); }}
              className="px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-[10px] font-bold"
            >
              Proteção Alta (15% + 400g)
            </button>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setIsEditingAdminConfig(false)}
              className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg font-bold"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSaveConfig}
              className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-bold shadow-xs"
            >
              Salvar Alterações
            </button>
          </div>
        </div>
      )}

      {/* Comparison Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        
        {/* Net Weight Box */}
        <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-1.5">
          <div className="flex justify-between items-center text-[11px] text-slate-500 font-bold uppercase tracking-wider">
            <span className="flex items-center gap-1">
              <Box className="w-3.5 h-3.5 text-slate-400" />
              Peso Líquido dos Itens
            </span>
            <span className="font-mono text-slate-700">{safeNetWeight.toFixed(2)} kg</span>
          </div>
          {recommendedNetCalc && (
            <div className="flex justify-between items-baseline pt-1">
              <span className="text-xs text-slate-600 font-medium truncate max-w-[150px]">
                {recommendedNetCalc.method.name}
              </span>
              <span className="font-mono font-bold text-slate-800 text-xs">
                ¥ {recommendedNetCalc.costJpy.toLocaleString()}
                <span className="text-[10px] font-normal text-slate-500 ml-1">
                  (~ R$ {recommendedNetCalc.totalCostBrl.toFixed(2)})
                </span>
              </span>
            </div>
          )}
        </div>

        {/* Suggested Gross Weight Box */}
        <div className="p-3 bg-gradient-to-r from-amber-500/10 to-amber-600/10 rounded-xl border border-amber-300 space-y-1.5 relative overflow-hidden">
          <div className="flex justify-between items-center text-[11px] text-amber-900 font-extrabold uppercase tracking-wider">
            <span className="flex items-center gap-1 text-amber-800">
              <Scale className="w-3.5 h-3.5 text-amber-600" />
              Peso Bruto Sugerido (com Margem)
            </span>
            <span className="font-mono text-amber-950 font-black bg-amber-200/80 px-2 py-0.5 rounded text-xs">
              {grossWeightKg.toFixed(2)} kg
            </span>
          </div>

          {recommendedGrossCalc && (
            <div className="flex justify-between items-baseline pt-1">
              <span className="text-xs text-amber-950 font-bold truncate max-w-[150px]">
                {recommendedGrossCalc.method.name}
              </span>
              <span className="font-mono font-extrabold text-amber-900 text-sm">
                ¥ {recommendedGrossCalc.costJpy.toLocaleString()}
                <span className="text-[11px] font-bold text-amber-800 ml-1">
                  (~ R$ {recommendedGrossCalc.totalCostBrl.toFixed(2)})
                </span>
              </span>
            </div>
          )}
        </div>

      </div>

      {/* Explanation Banner & Apply Button */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-1">
        <div className="text-[11px] text-slate-600 flex items-start gap-1.5 max-w-lg">
          {hasTierJump ? (
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          ) : (
            <HelpCircle className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
          )}
          <span>
            {hasTierJump ? (
              <strong className="text-amber-800 font-bold">Aviso de Faixa de Peso: </strong>
            ) : null}
            O peso bruto sugerido de <strong>{grossWeightKg.toFixed(2)} kg</strong> inclui <strong>+{marginWeight.toFixed(2)} kg</strong> (variação) e <strong>+{bufferKg.toFixed(2)} kg</strong> para caixa de papelão reforçada e fita adesiva.
          </span>
        </div>

        {onApplySuggestedWeight && (
          <button
            type="button"
            onClick={handleApply}
            className={`w-full sm:w-auto px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 shadow-sm shrink-0 ${
              appliedNotice
                ? 'bg-emerald-600 text-white'
                : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white'
            }`}
          >
            {appliedNotice ? (
              <>
                <Check className="w-4 h-4 stroke-[3]" />
                <span>Aplicado ({grossWeightKg.toFixed(2)} kg)</span>
              </>
            ) : (
              <>
                <span>Usar Frete Sugerido ({grossWeightKg.toFixed(2)} kg)</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </>
            )}
          </button>
        )}
      </div>

    </div>
  );
};

export default ShippingSafetyMarginSuggestion;
